import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import useAuth from 'hooks/useAuth';

declare global {
  interface Window {
    chatbase: any;
  }
}

const CHATBASE_CONFIG: Record<number, { scriptId: string; secretKey: string }> = {
  20: {
    scriptId: '8VmhZdd8-kQ2dR7m8mM0d',
    secretKey: 'ptzj2s1xmlb8zny4fnppqc29icog2oze',
  },
};

function base64UrlEncode(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const str = String.fromCharCode(...bytes);
  return base64UrlEncode(str);
}

async function generateJWT(payload: any, secretKey: string) {
  if (!window.crypto || !window.crypto.subtle) {
    console.warn(
      'Chatbase: window.crypto.subtle indisponível. Necessário HTTPS e navegador compatível.'
    );
    return '';
  }

  const header = { alg: 'HS256', typ: 'JWT' };

  const encoder = new TextEncoder();

  const headerStr = JSON.stringify(header);
  const payloadStr = JSON.stringify(payload);

  const headerB64 = base64UrlEncode(
    String.fromCharCode(...new Uint8Array(encoder.encode(headerStr)))
  );
  const payloadB64 = base64UrlEncode(
    String.fromCharCode(...new Uint8Array(encoder.encode(payloadStr)))
  );

  const dataToSign = `${headerB64}.${payloadB64}`;

  const keyData = encoder.encode(secretKey);
  const msgData = encoder.encode(dataToSign);

  const key = await window.crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await window.crypto.subtle.sign('HMAC', key, msgData);
  const signatureB64 = bufferToBase64Url(signature);

  return `${dataToSign}.${signatureB64}`;
}

const ChatbaseWidget: React.FC = () => {
  const { userInfo, networkId } = useAuth();
  const user = userInfo?.user;
  const authState = useSelector((state: any) => state.auth);

  useEffect(() => {
    // console.log('--- CHATBASE WIDGET EFFECT STARTED ---');
    // console.log('NetworkID:', networkId);
    // console.log('User:', user ? user.userId : 'Nenhum');

    const ALLOWED_NETWORKS = [20];
    if (!networkId || !ALLOWED_NETWORKS.includes(networkId)) {
      // console.log('Chatbase: Rede não permitida. Abortando.');
      Object.values(CHATBASE_CONFIG).forEach(({ scriptId }) => {
        const existingScript = document.getElementById(scriptId);
        if (existingScript) existingScript.remove();
      });
      return;
    }

    const config = CHATBASE_CONFIG[networkId]; // if (authState) { //   console.log('--- CHATBASE DEBUG (FULL AUTH STATE) ---'); //   console.log(authState); //   console.log('----------------------------------------'); // }

    const identifyUser = async () => {
      if (!user) {
        // console.warn('Chatbase: identifyUser chamado sem usuário.');
        return;
      } // console.log('Chatbase: Iniciando identificação do usuário...', user.userId);

      const userId = user.userId.toString();
      const jwtPayload = {
        user_id: userId,
        name: user.name,
        email: user.email,
        exp: Math.floor(Date.now() / 1000) + 60 * 60,
      };

      let token = '';
      if (config.secretKey) {
        try {
          token = await generateJWT(jwtPayload, config.secretKey); // console.log('--- CHATBASE DEBUG ---'); // console.log('User ID:', userId); // console.log('JWT Payload:', jwtPayload); // console.log('JWT Token:', token); // console.log('----------------------');
        } catch (e) {
          console.error('Erro ao gerar JWT do Chatbase:', e);
        }
      }

      if (token && window.chatbase) {
        try {
          // const [, payloadB64] = token.split('.');
          // const payloadJson = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
          // console.log('Chatbase identify -> públicos (visíveis ao bot):', {
          //   name: user.name,
          //   user_id: userId,
          // });
          // console.log('Chatbase JWT payload decodificado:', payloadJson);
        } catch (err) {
          console.warn('Chatbase: falha ao decodificar JWT para log:', err);
        } // console.log('Enviando identify para Chatbase...');

        window.chatbase('identify', {
          token: token,
          name: user.name,
          user_id: userId,
        });
      } else {
        console.warn(
          'Chatbase: Falha ao identificar. Token:',
          !!token,
          'Window.chatbase:',
          !!window.chatbase
        );
      }
    };

    const SCRIPT_ID = config.scriptId;
    const existingScript = document.getElementById(SCRIPT_ID);

    if (!existingScript) {
      (function () {
        if (!window.chatbase || window.chatbase('getState') !== 'initialized') {
          window.chatbase = (...args: any[]) => {
            if (!window.chatbase.q) {
              window.chatbase.q = [];
            }
            window.chatbase.q.push(args);
          };
          window.chatbase = new Proxy(window.chatbase, {
            get(target: any, prop: any) {
              if (prop === 'q') {
                return target.q;
              }
              return (...args: any[]) => target(prop, ...args);
            },
          });
        }
        const onLoad = function () {
          const script = document.createElement('script');
          script.src = 'https://www.chatbase.co/embed.min.js';
          script.id = SCRIPT_ID;
          (script as any).domain = 'www.chatbase.co';

          script.onload = () => {
            console.log('Chatbase: Script carregado. Disparando identificação.');
            identifyUser();
          };

          document.body.appendChild(script);
        };
        if (document.readyState === 'complete') {
          onLoad();
        } else {
          window.addEventListener('load', onLoad);
        }
      })();
    } else {
      identifyUser();
    }

    if (user && existingScript) {
      identifyUser();
    }

    return () => {};
  }, [user, networkId, authState]);

  return null;
};

export default ChatbaseWidget;
